/**
 * Demo-provision store — Zustand slice tracking in-flight SA demo provisioning
 * runs, keyed by slug.
 *
 * Why a store and not just the mutation hook: the founder flow is "click
 * Provision → route IMMEDIATELY to /demos/{slug} in a Provisioning state →
 * watch it flip to Ready". The provision POST is SYNCHRONOUS on the backend
 * (~60-100s: it INSERTs the tenant row in the first tx, then runs the long
 * seed + rank-one phase before returning). If the in-flight promise lived in
 * the DemoProvision form's `useMutation`, navigating away would unmount the
 * form and orphan the outcome — DemoDetail would have no way to learn whether
 * the run succeeded or failed.
 *
 * Lifting the run into a module-level store fixes that: the form fires
 * `startProvision`, navigates, and DemoDetail reads the run's state (and the
 * last submitted body, so a Failed run can be retried in place).
 *
 * The store is the DEFINITIVE Ready/Failed signal. The polled status endpoint
 * (`GET /admin/demo/{slug}/status`) is a SECONDARY signal used only when the
 * store has no live run (e.g. the SA reloaded the page mid-provision or reopened
 * it later): status `exists && last_reset_at && ranking_count>0` ⇒ Ready.
 * Status alone CANNOT distinguish Provisioning from Failed (the tenant row is
 * committed before seeding and is NOT rolled back on seed failure), which is
 * exactly why the store's POST outcome is authoritative.
 */
import { create } from "zustand";
import { apiClient } from "@/lib/apiClient";
import { ApiError } from "@/lib/apiError";
import { queryClient } from "@/lib/queryClient";

export type DemoProvisionState = "provisioning" | "ready" | "failed";

interface DemoProvisionBody {
  slug: string;
  admin_email: string;
  admin_first_name: string;
  admin_last_name: string;
  description?: string;
  confirm: "PROVISION DEMO";
}

interface DemoProvisionResponse {
  tenant_id: string;
  slug: string;
  admin_email: string;
  jds_seeded: number;
  candidates_seeded: number;
  rankings_seeded: number;
  initial_credits: number;
  audit_event_id: string;
  login_url: string;
}

interface DemoProvisionRun {
  state: DemoProvisionState;
  /** The body that started this run — retained so a Failed run can Retry. */
  body: DemoProvisionBody;
  result?: DemoProvisionResponse;
  error?: ApiError;
}

interface DemoProvisionStoreState {
  /** Keyed by slug — one run per slug at a time. */
  runs: Record<string, DemoProvisionRun>;
  /** Fire the provision POST and track its outcome under `body.slug`. */
  startProvision: (body: DemoProvisionBody) => Promise<void>;
  /** Clear a run (e.g. after the SA acknowledges a terminal state). */
  clearRun: (slug: string) => void;
}

export const useDemoProvisionStore = create<DemoProvisionStoreState>((set) => ({
  runs: {},

  startProvision: async (body) => {
    const { slug } = body;
    set((s) => ({
      runs: { ...s.runs, [slug]: { state: "provisioning", body } },
    }));

    try {
      const r = await apiClient.post<DemoProvisionResponse>(
        "/admin/demo/provision",
        body,
      );
      set((s) => ({
        runs: {
          ...s.runs,
          [slug]: { state: "ready", body, result: r.data },
        },
      }));
      // Keep the SA list + tenants surfaces fresh once the run lands.
      queryClient.invalidateQueries({ queryKey: ["admin", "demos"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "tenants"] });
      queryClient.invalidateQueries({ queryKey: ["admin", "demo", slug, "status"] });
    } catch (err) {
      const apiErr =
        err instanceof ApiError
          ? err
          : new ApiError({
              error: "provision_failed",
              message: "Demo tenant provisioning failed.",
              details: {},
              requestId: undefined,
              status: 500,
            });
      set((s) => ({
        runs: {
          ...s.runs,
          [slug]: { state: "failed", body, error: apiErr },
        },
      }));
    }
  },

  clearRun: (slug) => {
    set((s) => {
      if (!(slug in s.runs)) return s;
      const next = { ...s.runs };
      delete next[slug];
      return { runs: next };
    });
  },
}));
